
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, AlertTriangle, CheckCircle, Edit, Plus, X } from "lucide-react";
import { format } from "date-fns";
import { parseArrayField, safeFormatDate } from "@/lib/safeParse";

export default function APConsequences() {
  const [searchTerm, setSearchTerm] = useState("");
  const [consequenceFilter, setConsequenceFilter] = useState("all");
  const [message, setMessage] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  // New note entry
  const [newNoteDate, setNewNoteDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [newNoteText, setNewNoteText] = useState("");
  const [noteHistory, setNoteHistory] = useState([]);

  // Parent contacts
  const [newContactDate, setNewContactDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [parentContacts, setParentContacts] = useState([]);

  // Principal informed
  const [newPrincipalDate, setNewPrincipalDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [principalContacts, setPrincipalContacts] = useState([]);

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const apEmail = user?.email;
  const apCohort = user?.ap_grade_cohort;

  const { data: consequenceLevels = [] } = useQuery({
    queryKey: ['consequenceLevels'],
    queryFn: () => base44.entities.ConsequenceLevel.list(),
  });

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const { data: myStudents = [], isLoading } = useQuery({
    queryKey: ['myAPStudents', apEmail, apCohort, students], // Added students to queryKey for re-fetch if students data changes, though it's mainly for `enabled`
    queryFn: async () => {
      if (!apEmail) return [];
      const studentsData = await base44.entities.EAMissTracking.filter({ assigned_ap_email: apEmail }, '-created_date');

      // Look up names from Student database and add for sorting
      const processedStudents = studentsData.map(student => {
        const studentRecord = students.find(s => s.student_id === student.student_id);
        return {
          ...student,
          student_first_name: student.student_first_name || studentRecord?.first_name || '',
          student_last_name: student.student_last_name || studentRecord?.last_name || '',
        };
      });

      if (apCohort === '4-5') {
        return processedStudents.filter(student => {
          const grade = parseInt(student.grade_level);
          return grade === 4 || grade === 5;
        });
      } else if (apCohort === '6-8') {
        return processedStudents.filter(student => {
          const grade = parseInt(student.grade_level);
          return grade === 6 || grade === 7 || grade === 8;
        });
      }

      return [];
    },
    enabled: !!apEmail && !!apCohort && students.length > 0, // Enabled only if students data is available
  });

  const updateInterventionMutation = useMutation({
    mutationFn: async () => {
      return base44.entities.EAMissTracking.update(selectedStudent.id, {
        intervention_notes: JSON.stringify(noteHistory),
        parent_contacted: parentContacts.length > 0,
        parent_contact_date: parentContacts.length > 0 ? parentContacts[0] : undefined,
        parent_contact_history: JSON.stringify(parentContacts),
        detention_assigned: principalContacts.length > 0,
        detention_date: principalContacts.length > 0 ? principalContacts[0] : undefined,
        principal_contact_history: JSON.stringify(principalContacts),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myAPStudents']);
      setMessage({
        type: 'success',
        text: 'Consequence details updated successfully'
      });
      setSelectedStudent(null);
      setDialogOpen(false); // Close the dialog
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to update consequence details' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const requestDeletionMutation = useMutation({
    mutationFn: async ({ trackingId, reason }) => {
      const record = myStudents.find(s => s.id === trackingId);
      const notes = parseArrayField(record?.intervention_notes);

      const deletionNote = {
        date: format(new Date(), 'yyyy-MM-dd'),
        note: `🗑️ DELETION REQUESTED by ${user?.full_name}\nReason: ${reason}`,
        timestamp: new Date().toISOString()
      };

      notes.push(deletionNote);

      return base44.entities.EAMissTracking.update(trackingId, {
        intervention_notes: JSON.stringify(notes),
        status: 'escalated' // Mark as escalated so admin sees it
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myAPStudents']);
      setMessage({ type: 'success', text: 'Deletion request submitted to administrator' });
      setDialogOpen(false);
      setSelectedStudent(null);
      setTimeout(() => setMessage(null), 5000); // Clear message after some time
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to submit request: ${err.message}` });
      setTimeout(() => setMessage(null), 5000); // Clear message after some time
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ trackingId, newStatus }) => {
      return base44.entities.EAMissTracking.update(trackingId, {
        status: newStatus
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['myAPStudents']);
      setMessage({ type: 'success', text: 'Status updated' });
      setTimeout(() => setMessage(null), 3000);
    }
  });

  const getConsequenceDisplay = (consequence) => { // Renamed function
    const level = consequenceLevels.find(l => l.level_key === consequence);
    if (level) {
      return {
        color: level.badge_color,
        label: `${level.badge_emoji} ${level.level_name}`
      };
    }

    const badges = {
      warning: { color: 'bg-green-100 text-green-800', label: '⚠️ Warning' },
      parent_contact: { color: 'bg-yellow-100 text-yellow-800', label: '📞 Parent Contact' },
      detention: { color: 'bg-orange-100 text-orange-800', label: '📋 Detention' },
      iss: { color: 'bg-red-100 text-red-800', label: '🚫 ISS' },
      admin_review: { color: 'bg-red-900 text-white', label: '⛔ Admin Review' }
    };
    return badges[consequence] || { color: 'bg-slate-100 text-slate-800', label: consequence };
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-blue-100 text-blue-800', label: 'Active' },
      resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
      escalated: { color: 'bg-purple-100 text-purple-800', label: 'Escalated' }
    };
    return badges[status] || badges.active;
  };

  const handleViewDetails = (student) => { // Renamed function
    setSelectedStudent(student);
    setDialogOpen(true); // Open the dialog

    // Parse note / contact / principal-contact history defensively
    setNoteHistory(parseArrayField(student.intervention_notes));
    setParentContacts(parseArrayField(student.parent_contact_history));
    setPrincipalContacts(parseArrayField(student.principal_contact_history));

    // Reset new entry fields
    setNewNoteDate(format(new Date(), 'yyyy-MM-dd'));
    setNewNoteText("");
    setNewContactDate(format(new Date(), 'yyyy-MM-dd'));
    setNewPrincipalDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const addNote = () => {
    if (!newNoteText.trim()) return;

    const newNote = {
      date: newNoteDate,
      note: newNoteText.trim(),
      timestamp: new Date().toISOString()
    };

    setNoteHistory([...noteHistory, newNote]);
    setNewNoteText("");
    setNewNoteDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const removeNote = (index) => {
    setNoteHistory(noteHistory.filter((_, i) => i !== index));
  };

  const addParentContact = () => {
    if (!newContactDate) return;
    setParentContacts([...parentContacts, newContactDate]);
    setNewContactDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const removeParentContact = (index) => {
    setParentContacts(parentContacts.filter((_, i) => i !== index));
  };

  const addPrincipalContact = () => {
    if (!newPrincipalDate) return;
    setPrincipalContacts([...principalContacts, newPrincipalDate]);
    setNewPrincipalDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const removePrincipalContact = (index) => {
    setPrincipalContacts(principalContacts.filter((_, i) => i !== index));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    if (sortConfig.direction === 'ascending') {
      return <span className="ml-1">▲</span>;
    }
    return <span className="ml-1">▼</span>;
  };

  const activeStudents = myStudents.filter(s => s.status === 'active' || s.status === 'escalated');
  const resolvedStudents = myStudents.filter(s => s.status === 'resolved');
  const highPriorityCasesCount = activeStudents.filter(s => s.current_consequence === 'iss' || s.current_consequence === 'admin_review' || s.status === 'escalated').length; // Renamed for clarity

  let filteredActiveStudents = activeStudents.filter(student => {
    const matchesSearch =
      student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.student_id?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesConsequence = consequenceFilter === 'all' || student.current_consequence === consequenceFilter;

    return matchesSearch && matchesConsequence;
  });

  // Apply sorting to active students
  const sortedRecords = [...filteredActiveStudents].sort((a, b) => {
    if (!sortConfig.key) {
      return 0;
    }

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    // Handle numeric comparisons
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
    }

    // Handle string comparisons
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'ascending'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Fallback to string comparison
    const stringA = String(aValue || '').toLowerCase();
    const stringB = String(bValue || '').toLowerCase();
    return sortConfig.direction === 'ascending'
      ? stringA.localeCompare(stringB)
      : stringB.localeCompare(aValue);
  });

  // Filter for the resolved table
  const resolvedRecords = resolvedStudents.filter(student => {
    const matchesSearch =
      student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.student_id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });


  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">EA Consequences</h1>
          <p className="text-slate-600 mt-1">Manage EA Refusals for Your Grade Cohort</p>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-none shadow-lg">
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Active Cases</div>
              <div className="text-3xl font-bold text-blue-600">{activeStudents.length}</div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-lg">
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">High Priority</div>
              <div className="text-3xl font-bold text-red-600">{highPriorityCasesCount}</div>
              <div className="text-xs text-slate-500 mt-1">ISS, Admin Review, or Escalated</div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-lg">
            <CardContent className="p-4">
              <div className="text-sm text-slate-600">Resolved</div>
              <div className="text-3xl font-bold text-green-600">{resolvedStudents.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Active Students Section */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Active Cases</CardTitle>
            <div className="flex flex-col md:flex-row gap-4 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by student name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={consequenceFilter} onValueChange={setConsequenceFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Consequence Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {consequenceLevels.length > 0 ? (
                    consequenceLevels.map((level) => (
                      <SelectItem key={level.id} value={level.level_key}>
                        {level.badge_emoji} {level.level_name}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="warning">⚠️ Warning</SelectItem>
                      <SelectItem value="parent_contact">📞 Parent Contact</SelectItem>
                      <SelectItem value="detention">📋 Detention</SelectItem>
                      <SelectItem value="iss">🚫 ISS</SelectItem>
                      <SelectItem value="admin_review">⛔ Admin Review</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_last_name')}
                    >
                      <div className="flex items-center">
                        Last Name
                        {getSortIcon('student_last_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_first_name')}
                    >
                      <div className="flex items-center">
                        First Name
                        {getSortIcon('student_first_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('grade_level')}
                    >
                      <div className="flex items-center">
                        Grade
                        {getSortIcon('grade_level')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('miss_count')}
                    >
                      <div className="flex items-center">
                        Refusals
                        {getSortIcon('miss_count')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('current_consequence')}
                    >
                      <div className="flex items-center">
                        Consequence
                        {getSortIcon('current_consequence')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('makeup_days_owed')}
                    >
                      <div className="flex items-center">
                        Makeup Days
                        {getSortIcon('makeup_days_owed')}
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        {searchTerm ? 'No students match your search' : 'No students assigned to you'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRecords.map((record) => {
                      const remaining = (record.makeup_days_owed || 0) - (record.makeup_days_completed || 0);
                      const highPriority = remaining > 2 || record.miss_count >= 4 || record.status === 'escalated';

                      return (
                        <TableRow key={record.id} className={highPriority ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}>
                          <TableCell className="font-medium">{record.student_last_name || '-'}</TableCell>
                          <TableCell>{record.student_first_name || '-'}</TableCell>
                          <TableCell>{record.grade_level}</TableCell>
                          <TableCell>
                            <Badge className="bg-red-600 text-white font-bold">
                              {record.miss_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getConsequenceDisplay(record.current_consequence).color}>
                              {getConsequenceDisplay(record.current_consequence).label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={remaining > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                              {record.makeup_days_completed || 0} / {record.makeup_days_owed || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={record.status}
                              onValueChange={(value) =>
                                updateStatusMutation.mutate({ trackingId: record.id, newStatus: value })
                              }
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                                <SelectItem value="escalated">Escalated</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {highPriority && (
                              <Badge className="bg-red-600 text-white">
                                🔥 High
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleViewDetails(record)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Resolved/Archived Students Section */}
        <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-green-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-900">
              <CheckCircle className="w-5 h-5" />
              Resolved & Archived Cases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Last Name</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>Refusals</TableHead>
                    <TableHead>Consequence</TableHead>
                    <TableHead>Makeup Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No resolved cases yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    resolvedRecords.map((record) => {
                      const remaining = (record.makeup_days_owed || 0) - (record.makeup_days_completed || 0);
                      return (
                        <TableRow key={record.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{record.student_last_name || '-'}</TableCell>
                          <TableCell>{record.student_first_name || '-'}</TableCell>
                          <TableCell>{record.grade_level}</TableCell>
                          <TableCell>
                            <Badge className="bg-red-600 text-white font-bold">
                              {record.miss_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getConsequenceDisplay(record.current_consequence).color}>
                              {getConsequenceDisplay(record.current_consequence).label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={remaining > 0 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                              {record.makeup_days_completed || 0} / {record.makeup_days_owed || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(record.status).color}>
                              {getStatusBadge(record.status).label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleViewDetails(record)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>


        {/* Consequence Levels Info */}
        <Card className="border-none shadow-lg bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Consequence Levels & Actions (REFUSALS only):</h3>
            {consequenceLevels.length > 0 ? (
              <div className="space-y-2 text-sm text-slate-700">
                {consequenceLevels.map((level) => (
                  <div key={level.id} className="flex items-center gap-2">
                    <Badge className={level.badge_color}>
                      {level.badge_emoji} {level.level_name}
                    </Badge>
                    <span>
                      {level.refusal_threshold} refusal{level.refusal_threshold > 1 ? 's' : ''} - {level.level_description}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">⚠️ Warning</Badge>
                  <span>1 refusal - Conference with student, document + 1 makeup day</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-100 text-yellow-800">📞 Parent Contact</Badge>
                  <span>2 refusals - Phone call to parent/guardian + 2 makeup days</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-orange-100 text-orange-800">📋 Detention</Badge>
                  <span>3 refusals - Assign detention, notify parent + 3 makeup days</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-800">🚫 ISS</Badge>
                  <span>4 refusals - In-School Suspension recommendation + 4 makeup days</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-900 text-white">⛔ Admin Review</Badge>
                  <span>5+ refusals - Escalate to admin, OSS consideration + 5+ makeup days</span>
                </div>
              </div>
            )}
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded">
              <p className="text-sm font-semibold text-orange-900">📋 Remember: Each REFUSAL (R) = 1 makeup day required. Track completed makeup days in the intervention dialog.</p>
            </div>
          </CardContent>
        </Card>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* The DialogTrigger should ideally be inside each TableCell action button, not globally here */}
        {/* However, the current setup uses an `open` prop on Dialog, controlled by `dialogOpen` */}
        {/* and `selectedStudent` in `handleViewDetails` sets `dialogOpen(true)`. This is fine. */}
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedStudent && (
            <>
              <DialogHeader>
                <DialogTitle>Consequence Details - {selectedStudent.student_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600">Student ID</p>
                    <p className="font-semibold">{selectedStudent.student_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Grade Level</p>
                    <p className="font-semibold">{selectedStudent.grade_level}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Refusals</p>
                    <p className="font-semibold text-red-600">{selectedStudent.miss_count}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Consequence Level</p>
                    <Badge className={getConsequenceDisplay(selectedStudent.current_consequence).color}>
                      {getConsequenceDisplay(selectedStudent.current_consequence).label}
                    </Badge>
                  </div>
                </div>

                {/* Makeup Days Tracking */}
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2">📋 Makeup Days Tracking</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-slate-600">Days Owed</p>
                      <p className="text-2xl font-bold text-orange-600">{selectedStudent.makeup_days_owed || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Days Completed</p>
                      <p className="text-2xl font-bold text-green-600">{selectedStudent.makeup_days_completed || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Remaining</p>
                      <p className="text-2xl font-bold text-red-600">
                        {(selectedStudent.makeup_days_owed || 0) - (selectedStudent.makeup_days_completed || 0)}
                      </p>
                    </div>
                  </div>
                  <Alert className="bg-blue-50 border-blue-200 mt-3">
                    <AlertDescription className="text-sm text-blue-900">
                      ✅ Makeup days are automatically tracked when student attends EA. No manual entry needed.
                    </AlertDescription>
                  </Alert>
                </div>

                {/* Parent Contacts */}
                <div className="space-y-2">
                  <Label>Parent Contacts</Label>
                  {parentContacts.length > 0 && (
                    <div className="space-y-2">
                      {parentContacts.map((date, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded">
                          <span className="text-sm font-medium">
                            📞 Contacted: {safeFormatDate(date)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeParentContact(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newContactDate}
                      onChange={(e) => setNewContactDate(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={addParentContact} size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Contact
                    </Button>
                  </div>
                </div>

                {/* Principal Informed */}
                <div className="space-y-2">
                  <Label>Principal Informed</Label>
                  {principalContacts.length > 0 && (
                    <div className="space-y-2">
                      {principalContacts.map((date, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                          <span className="text-sm font-medium">
                            👤 Informed: {safeFormatDate(date)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removePrincipalContact(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newPrincipalDate}
                      onChange={(e) => setNewPrincipalDate(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={addPrincipalContact} size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Entry
                    </Button>
                  </div>
                </div>

                {/* Consequence Notes with Date */}
                <div className="space-y-2">
                  <Label>Consequence Notes</Label>

                  {/* Existing notes */}
                  {noteHistory.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {noteHistory.map((entry, index) => (
                        <div key={index} className="p-3 bg-slate-50 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-slate-500 mb-1">
                                {safeFormatDate(entry?.date)}
                              </p>
                              <p className="text-sm text-slate-900 whitespace-pre-wrap">{entry?.note}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeNote(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new note */}
                  <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Label htmlFor="new-note-date" className="text-sm">Add New Entry</Label>
                    <Input
                      id="new-note-date"
                      type="date"
                      value={newNoteDate}
                      onChange={(e) => setNewNoteDate(e.target.value)}
                    />
                    <Textarea
                      id="new-note"
                      placeholder="Enter consequence notes, interventions, actions taken..."
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      className="h-24"
                    />
                    <Button onClick={addNote} size="sm" className="w-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Note Entry
                    </Button>
                  </div>
                </div>

                {(() => {
                  const missDates = parseArrayField(selectedStudent.miss_history);
                  if (missDates.length === 0) return null;
                  return (
                    <div>
                      <Label>Refusal History</Label>
                      <div className="text-sm text-slate-600 mt-1">
                        {missDates.map((date, idx) => (
                          <span key={idx} className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded mr-2 mb-2">
                            {safeFormatDate(date)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const makeupDates = parseArrayField(selectedStudent.makeup_dates);
                  if (makeupDates.length === 0) return null;
                  return (
                    <div>
                      <Label>Makeup Days Completed</Label>
                      <div className="text-sm text-slate-600 mt-1">
                        {makeupDates.map((date, idx) => (
                          <span key={idx} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded mr-2 mb-2">
                            ✓ {safeFormatDate(date)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <Button
                  onClick={() => updateInterventionMutation.mutate()}
                  className="w-full bg-red-700 hover:bg-red-800"
                  disabled={updateInterventionMutation.isPending}
                >
                  {updateInterventionMutation.isPending ? 'Saving...' : 'Save Consequence Details'}
                </Button>

                {/* AP Request Deletion */}
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-semibold text-orange-900 mb-2">Request Record Deletion</p>
                  <p className="text-xs text-orange-700 mb-3">
                    If this record was created by mistake or should be removed, request deletion from the administrator.
                  </p>
                  <Button
                    onClick={() => {
                      const reason = prompt('Please explain why this record should be deleted:');
                      if (reason !== null) { // Check if user clicked 'Cancel'
                        if (reason.trim()) {
                          requestDeletionMutation.mutate({ 
                            trackingId: selectedStudent.id, 
                            reason: reason.trim() 
                          });
                        } else {
                          alert('You must provide a reason for deletion.');
                        }
                      }
                    }}
                    variant="outline"
                    className="w-full border-orange-300 text-orange-700 hover:bg-orange-100"
                    disabled={requestDeletionMutation.isPending}
                  >
                    {requestDeletionMutation.isPending ? 'Submitting Request...' : 'Request Deletion from Admin'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
